import axios from "axios";
import { StudentUser } from "interfaces/users";
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Reusable hook for search logic for finding students.
 */
export const useStudentSearch = () => {
  const [loadingStudents, setLoadingStudents] = useState<boolean>(true);
  const [searchValue, setSearchValue] = useState<string>("");
  const [students, setStudents] = useState<StudentUser[] | undefined>(undefined);
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout>>();

  const handleStudentSearch = useCallback((value: string) => {
    // Debounce search to prevent too many requests
    setLoadingStudents(true);
    setStudents(undefined);
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }
    timeoutIdRef.current = setTimeout(
      () => {
        setSearchValue(value);
      },
      value.length ? 300 : 0
    );
  }, []);

  useEffect(
    function fetchStudents() {
      if (searchValue === "") {
        setStudents(undefined);
        setLoadingStudents(false);
        return;
      }
      setLoadingStudents(true);
      const params = new URLSearchParams({
        has_graduated: "false",
      });

      if (searchValue) {
        params.append("search", searchValue);
      }

      axios
        .get<{ results: StudentUser[] }>(`/students/?${params.toString()}`)
        .then(response => {
          setStudents(response.data.results);
          setLoadingStudents(false);
        })
        .catch(() => {
          setLoadingStudents(false);
        });
    },
    [searchValue]
  );

  return {
    loadingStudents,
    handleStudentSearch,
    students,
  };
};
